/**
 * The visit's **Materials** tab — `docs/visit-page.md` §8 and §9; Boards 2,
 * 3 A – C, 4, 16 C and 18 B, C.
 *
 * *By status · By day* (remembered per person, on the server), *Find a
 * material*, the billing chip, **Add a material** and **Order what's needed**;
 * the summary strip; then the table by status (Board 2) or the crew's morning
 * by day (Board 3 A). The tab owns its sheets — Swap (Board 4), Order (Board 3
 * B / 18 B), the ⋯ menus (§8.3; Board 18 C while purchasing is off) and the
 * undrawn sheets the ⋯ opens — and Board 3 C's toast.
 *
 * Every figure, count and state is the server's (`GET /visits/:id/materials`);
 * each write answers the tab re-read (§5.2), which is what the tab then draws.
 *
 * Measured by the panel, not the window (§19.1): below 1000 px of panel — an
 * iPad — the table takes Board 18 C's compact row. On a phone (Board 16 C) the
 * groups are cards and the sheets come from the bottom.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useSearchParams } from 'react-router-dom'
import type {
  MaterialLine, MaterialsByStatus, MaterialsRead, MaterialsView, OrderAnswer, VisitRead,
} from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { placeFloating } from '../lib/place-floating.js'
import { useDismiss } from '../components/overlays.js'
import { useVisitTab, useVisitWrite, type VisitWriteResult } from './api.js'
import { VisitGlyph, type VisitGlyphName } from './glyphs.js'
import { MATERIALS } from './copy.js'
import { money } from './format.js'
import {
  DeltaText, GROUP_DOT, count, poHref,
} from './materials-parts.js'
import { InternalChip } from './internal-chip.js'
import { FIELD_INPUT } from './fields.js'
import { LineOpened, StatusTable, onNotedOrder, type LineActions } from './materials-table.js'
import { ByDay } from './materials-by-day.js'
import { PhoneChips, PhoneStatus, type PhoneFilter } from './materials-phone.js'
import { SwapSheet } from './swap-sheet.js'
import { OrderSheet, OrderToast, type OrderRunPo } from './order-sheet.js'
import {
  AddMaterialSheet, EditLineSheet, MoveLineSheet, RemoveLineSheet, SpreadSheet,
} from './material-line-sheets.js'

/** Below this many pixels of panel the table is Board 18 C's compact row (§19.1, lead decisions round 5). */
const COMPACT_BELOW = 1000

type LineSheet = { kind: 'edit' | 'spread' | 'move' | 'remove' | 'swap'; line: MaterialLine }

export function MaterialsTab({ visitId, read, phone }: { visitId: string; read: VisitRead; phone: boolean }) {
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  /*
   * Undefined: the view this person last chose, which the server remembers
   * (§8.1). `?view=day` — the crew iPad card's *Load list* — opens By day.
   */
  const [view, setView] = useState<MaterialsView | undefined>(() => {
    const asked = params.get('view')
    return asked === 'day' || asked === 'status' ? asked : undefined
  })
  const [find, setFind] = useState('')
  const [q, setQ] = useState('')
  useEffect(() => {
    const timer = window.setTimeout(() => setQ(find.trim()), 200)
    return () => window.clearTimeout(timer)
  }, [find])
  const tab = useVisitTab<MaterialsRead>(visitId, '/materials', { view, q })
  const write = useVisitWrite(visitId)
  const data = tab.data
  const shownView: MaterialsView = data?.view ?? view ?? 'status'
  const canEdit = read.can.edit

  /* The panel's own width decides the compact row (§19.1: cards measured by the panel). */
  const root = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState<number | null>(null)
  useLayoutEffect(() => {
    const node = root.current
    if (!node) return undefined
    const measure = () => setWidth(node.getBoundingClientRect().width)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(node)
    return () => observer.disconnect()
  }, [])
  const compact = !phone && width !== null && width < COMPACT_BELOW

  const [openLine, setOpenLine] = useState<string | null>(params.get('line'))
  const [phoneFilter, setPhoneFilter] = useState<PhoneFilter>('all')
  const [menu, setMenu] = useState<{ line: MaterialLine; anchor: HTMLElement } | null>(null)
  const [sheet, setSheet] = useState<LineSheet | null>(null)
  const [adding, setAdding] = useState(false)
  /*
   * Order what's needed (§8.1, lead decision round 7): one drawn sheet per
   * line, vendor by vendor; each sheet's PO is kept in the run so a later line
   * of that vendor's joins it.
   */
  const [orderQueue, setOrderQueue] = useState<string[]>([])
  const [orderRun, setOrderRun] = useState<OrderRunPo[]>([])
  const orderLines = useCallback((ids: string[]) => { setOrderRun([]); setOrderQueue(ids) }, [])
  const [toast, setToast] = useState<OrderAnswer['ordered'] | null>(null)
  const [moveError, setMoveError] = useState<string | null>(null)

  const take = useCallback((result: VisitWriteResult) => {
    if (result.answer?.tab.key === 'materials') tab.refresh(result.answer.tab.read as MaterialsRead)
    else tab.refresh()
  }, [tab])

  const toOrderIds = useMemo(() => toOrder(data), [data])

  /* Deep links (lead decisions, rounds 1 and 5): ?order=next, ?add=1, ?line=<id>. */
  useEffect(() => {
    if (!data) return
    const order = params.get('order')
    const add = params.get('add')
    if (!order && !add) return
    const next = new URLSearchParams(params)
    if (order) {
      const ids = order === 'next' ? toOrderIds : [order]
      if (ids.length > 0) orderLines(ids)
      next.delete('order')
    }
    if (add) {
      setAdding(true)
      next.delete('add')
    }
    setParams(next, { replace: true })
  }, [data, params, setParams, toOrderIds, orderLines])

  /* A line opened by its address (Purchasing's link to it) is scrolled in. */
  useEffect(() => {
    const wanted = params.get('line')
    if (!wanted || !data) return
    setOpenLine(wanted)
    const node = root.current?.querySelector(`[data-line="${CSS.escape(wanted)}"]`)
    node?.scrollIntoView({ block: 'center' })
  }, [data, params])

  const chooseView = (next: MaterialsView) => { setView(next); setOpenLine(null) }

  const actions: LineActions = {
    order: (line) => orderLines([line.id]),
    swap: (line) => setSheet({ kind: 'swap', line }),
    menu: (line, anchor) => setMenu({ line, anchor }),
    menuFor: menu?.line.id ?? null,
  }

  async function movePo(line: MaterialLine, to: 'ready_for_pickup' | 'delivered_site' | 'delivered_yard') {
    if (!line.po) return
    setMoveError(null)
    try {
      const result = await write('POST', `/purchase-orders/${encodeURIComponent(line.po.id)}/move`, { to, visitId: read.id }, { tab: 'materials' })
      take(result)
    } catch (cause) {
      setMoveError(cause instanceof Error ? cause.message : String(cause))
    }
  }

  async function takeOff(line: MaterialLine) {
    setMoveError(null)
    try {
      const result = await write('POST', `/materials/${encodeURIComponent(line.id)}/order/remove`, { version: line.version }, { tab: 'materials' })
      take(result)
    } catch (cause) {
      setMoveError(cause instanceof Error ? cause.message : String(cause))
    }
  }

  /** The ⋯ of a line — §8.3's, or Board 18 C's on a noted PO while purchasing is off. */
  const menuItems = (line: MaterialLine): MenuEntry[] => {
    if (onNotedOrder(line, read) && line.po) {
      const po = line.po
      const vendor = line.vendor?.shortName ?? ''
      return [
        { glyph: 'pickup', title: MATERIALS.notedMenu.readyForPickup, sub: MATERIALS.notedMenu.readyForPickupSub(vendor), run: () => void movePo(line, 'ready_for_pickup') },
        { glyph: 'site', title: MATERIALS.notedMenu.deliveredSite, run: () => void movePo(line, 'delivered_site') },
        { glyph: 'yard', title: MATERIALS.notedMenu.deliveredYard, run: () => void movePo(line, 'delivered_yard') },
        'divider',
        {
          glyph: 'cart',
          title: MATERIALS.notedMenu.open(po.number),
          ...(po.vendorRef ? { sub: MATERIALS.notedMenu.openSub(po.vendorRef) } : {}),
          run: () => navigate(poHref(po)),
        },
        { glyph: 'close', title: MATERIALS.notedMenu.takeOff, sub: MATERIALS.notedMenu.takeOffSub(vendor), run: () => void takeOff(line) },
      ]
    }
    return [
      { title: MATERIALS.menu.edit, run: () => setSheet({ kind: 'edit', line }) },
      { title: MATERIALS.menu.spread, run: () => setSheet({ kind: 'spread', line }) },
      ...(line.actions.swapButton ? [] : [{ title: MATERIALS.menu.swap, run: () => setSheet({ kind: 'swap', line }) }]),
      { title: MATERIALS.menu.move, run: () => setSheet({ kind: 'move', line }) },
      { title: MATERIALS.menu.remove, run: () => setSheet({ kind: 'remove', line }) },
    ]
  }

  const orderingNow = orderQueue[0] ?? null

  return (
    <div ref={root} className={cn('flex min-w-0 flex-col leading-[normal] tabular-nums', phone ? 'gap-0' : 'gap-4')} data-tab="materials">
      {phone ? (
        data && (
          <PhoneChips
            view={shownView}
            filter={phoneFilter}
            toOrder={data.view === 'status' ? data.summary.needs_ordering : data.toolbar.toOrder}
            onTheWay={data.view === 'status' ? data.summary.on_the_way : 0}
            onView={chooseView}
            onFilter={setPhoneFilter}
          />
        )
      ) : (
        <Toolbar
          read={read}
          view={shownView}
          onView={chooseView}
          find={find}
          onFind={setFind}
          toOrder={data?.toolbar.toOrder ?? 0}
          canAdd={(data?.toolbar.canAdd ?? false) && canEdit}
          onAdd={() => setAdding(true)}
          onOrderAll={() => orderLines(toOrderIds)}
          billing={data?.toolbar.billing ?? read.billing}
        />
      )}

      {!phone && data?.view === 'status' && <SummaryStrip data={data} compact={compact} />}

      {moveError && <p className="m-0 text-[12.5px] text-danger">{moveError}</p>}

      {data?.view === 'status' && !phone && (
        <StatusTable
          data={data}
          read={read}
          compact={compact}
          openLine={openLine}
          onOpenLine={setOpenLine}
          actions={actions}
          canEdit={canEdit}
        />
      )}

      {data?.view === 'status' && phone && (
        <PhoneStatus
          data={data}
          read={read}
          filter={phoneFilter}
          openLine={openLine}
          onOpenLine={setOpenLine}
          onOrder={(line) => orderLines([line.id])}
          canEdit={canEdit}
          renderOpened={(line) => (
            <LineOpened line={line} read={read} compact>
              {canEdit && (
                <div className="overflow-hidden rounded-[10px] border border-line bg-surface">
                  {(line.actions.swapButton
                    ? [{ title: MATERIALS.swap, run: () => setSheet({ kind: 'swap', line }) } as MenuEntry, ...menuItems(line)]
                    : menuItems(line)
                  ).map((entry, index) => (entry === 'divider'
                    ? <div key={index} className="h-px bg-line" />
                    : (
                      <button
                        key={index}
                        type="button"
                        onClick={entry.run}
                        className="flex min-h-11 w-full items-center gap-[11px] border-t border-line-faint px-3 text-left first:border-t-0"
                      >
                        {entry.glyph && <span className="inline-flex text-text-muted"><VisitGlyph name={entry.glyph} size={17} stroke={2} /></span>}
                        <span className="flex flex-col py-2">
                          <b className="text-[14px] font-semibold">{entry.title}</b>
                          {entry.sub && <span className="text-[12px] text-text-muted">{entry.sub}</span>}
                        </span>
                      </button>
                    )))}
                </div>
              )}
            </LineOpened>
          )}
        />
      )}

      {data?.view === 'day' && (
        <ByDay data={data} read={read} phone={phone} onOrder={(lineId) => orderLines([lineId])} canEdit={canEdit} />
      )}

      {menu && (
        <LineMenu
          anchor={menu.anchor}
          entries={menuItems(menu.line)}
          asDrawn={onNotedOrder(menu.line, read) ? 'vp-18 C menu' : undefined}
          onClose={() => setMenu(null)}
        />
      )}

      {sheet?.kind === 'swap' && (
        <SwapSheet read={read} line={sheet.line} phone={phone} onClose={() => setSheet(null)} onDone={take} />
      )}
      {sheet?.kind === 'edit' && <EditLineSheet read={read} line={sheet.line} phone={phone} onClose={() => setSheet(null)} onDone={take} />}
      {sheet?.kind === 'spread' && <SpreadSheet read={read} line={sheet.line} phone={phone} onClose={() => setSheet(null)} onDone={take} />}
      {sheet?.kind === 'move' && <MoveLineSheet read={read} line={sheet.line} phone={phone} onClose={() => setSheet(null)} onDone={take} />}
      {sheet?.kind === 'remove' && <RemoveLineSheet read={read} line={sheet.line} phone={phone} onClose={() => setSheet(null)} onDone={take} />}
      {adding && <AddMaterialSheet read={read} phone={phone} onClose={() => setAdding(false)} onDone={take} />}

      {orderingNow && (
        <OrderSheet
          key={orderingNow}
          read={read}
          lineId={orderingNow}
          phone={phone}
          run={orderRun}
          onClose={() => setOrderQueue((queue) => queue.slice(1))}
          onDone={(result, ordered, joined) => {
            take(result)
            if (ordered) setToast(ordered)
            if (joined) setOrderRun((run) => [...run.filter((entry) => entry.vendor.id !== joined.vendor.id), joined])
          }}
        />
      )}

      {toast && (
        <OrderToast
          read={read}
          ordered={toast}
          phone={phone}
          onUndone={take}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  )
}

/**
 * The *To order* lines *Order what's needed* walks through: vendor by vendor
 * (§8.1) — each vendor where its first line is drawn, its lines in drawn
 * order; lines with no vendor yet last. By day knows a line's vendor from the
 * pickup stop it sits under.
 */
function toOrder(data: MaterialsRead | undefined): string[] {
  if (!data) return []
  const drawn: { id: string; vendorId: string | null }[] = data.view === 'status'
    ? data.groups.flatMap((group) => group.lines
      .filter((line) => line.state === 'to_order')
      .map((line) => ({ id: line.id, vendorId: line.vendor?.id ?? null })))
    : data.days.flatMap((day) => day.groups.flatMap((group) => group.rows
      .filter((row) => row.order)
      .map((row) => ({ id: row.lineId, vendorId: group.kind === 'pick_up' ? group.vendor.id : null }))))
  const lines = drawn.filter((line, index) => drawn.findIndex((other) => other.id === line.id) === index)
  const vendors = [...new Set(lines.map((line) => line.vendorId).filter((id): id is string => id !== null))]
  return [
    ...vendors.flatMap((vendorId) => lines.filter((line) => line.vendorId === vendorId)),
    ...lines.filter((line) => line.vendorId === null),
  ].map((line) => line.id)
}

// ---------------------------------------------------------------------------
// The toolbar (§8.1)
// ---------------------------------------------------------------------------

function Toolbar({ read, view, onView, find, onFind, toOrder: n, canAdd, onAdd, onOrderAll, billing }: {
  read: VisitRead
  view: MaterialsView
  onView: (view: MaterialsView) => void
  find: string
  onFind: (value: string) => void
  toOrder: number
  canAdd: boolean
  onAdd: () => void
  onOrderAll: () => void
  billing: VisitRead['billing']
}) {
  return (
    <div className="flex flex-wrap items-center gap-2.5">
      <span role="tablist" aria-label={MATERIALS.views.status} className="inline-flex shrink-0 rounded-[10px] bg-segmented-track p-[3px]">
        {(['status', 'day'] as const).map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={view === key}
            onClick={() => onView(key)}
            className={cn(
              'inline-flex h-[26px] items-center justify-center whitespace-nowrap rounded-button px-[11px] text-[12.5px] font-semibold',
              view === key ? 'bg-surface text-text shadow-[0_1px_2px_rgba(20,20,10,.12)]' : 'text-text-muted hover:text-text',
            )}
          >
            {MATERIALS.views[key]}
          </button>
        ))}
      </span>
      <label className="flex h-9 w-60 shrink-0 items-center gap-2 rounded-[10px] border border-line bg-surface px-[11px] text-[13px] text-text-faint focus-within:border-accent focus-within:shadow-[0_0_0_3px_rgb(var(--color-accent-ring))]">
        <VisitGlyph name="search" size={15} stroke={2} />
        <input
          value={find}
          onChange={(event) => onFind(event.target.value)}
          placeholder={MATERIALS.find}
          aria-label={MATERIALS.find}
          className={cn(FIELD_INPUT, 'text-text')}
        />
      </label>
      {billing !== 'per_visit' && (
        <span className="inline-flex h-[30px] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-line-soft px-2.5 text-[12px] font-semibold text-text-body">
          {billing === 'fixed' && <VisitGlyph name="lock" size={12} stroke={2.2} />}
          {billing === 'fixed' ? MATERIALS.fixedChip : MATERIALS.timeAndMaterialsChip}
        </span>
      )}
      <span className="flex-1" />
      {canAdd && (
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex h-9 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-[10px] border border-line bg-surface px-3.5 text-[12.5px] font-semibold text-text hover:bg-hover"
        >
          <VisitGlyph name="plus" size={14.5} stroke={2} />
          {MATERIALS.add}
        </button>
      )}
      {n > 0 && read.can.edit && (
        <button
          type="button"
          onClick={onOrderAll}
          className="inline-flex h-9 shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-[10px] border border-accent bg-accent px-3.5 text-[12.5px] font-semibold text-white hover:bg-accent-dark"
        >
          <VisitGlyph name="cart" size={14.5} stroke={2} />
          {MATERIALS.orderNeeded(count(n))}
        </button>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// The summary strip (§8.1)
// ---------------------------------------------------------------------------

function SummaryStrip({ data, compact }: { data: MaterialsByStatus; compact: boolean }) {
  const keys = ['needs_ordering', 'on_the_way', 'ready', 'bought', 'used'] as const
  return (
    <div className={cn('flex gap-2.5', compact && 'flex-wrap')}>
      {keys.map((key) => (
        <div
          key={key}
          className={cn('flex flex-1 items-center gap-2.5 rounded-[12px] border border-line bg-surface px-3.5 py-2.5',
            compact ? 'basis-[calc(33.333%-7px)]' : 'basis-0')}
        >
          <i aria-hidden="true" className={cn('inline-block h-2.5 w-2.5 shrink-0 rounded-full', GROUP_DOT[key])} />
          <b className="text-[20px]">{count(data.summary[key])}</b>
          <span className="whitespace-nowrap text-[12.5px] text-text-body">{MATERIALS.groups[key]}</span>
        </div>
      ))}
      {data.internal && (
        <div className={cn('flex flex-[1.5] flex-col justify-center gap-0.5 rounded-[12px] border border-line bg-surface-internal px-3.5 py-2',
          compact ? 'basis-[calc(66.666%-4px)]' : 'basis-0')}>
          <div className="flex items-center gap-1.5">
            <InternalChip />
            <span className="whitespace-nowrap text-[11.5px] text-text-muted">{MATERIALS.estimatedTile(money(data.internal.summary.estimated))}</span>
          </div>
          <div className="flex items-center gap-2">
            <b className="text-[15px]">{money(data.internal.summary.now)}</b>
            <span className="text-[12px] text-text-muted">{MATERIALS.now}</span>
            <DeltaText delta={data.internal.summary.delta} className="whitespace-nowrap text-right font-bold" />
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// A line's ⋯ (§8.3; Board 18 C)
// ---------------------------------------------------------------------------

type MenuEntry = 'divider' | { glyph?: VisitGlyphName; title: string; sub?: string; run: () => void }

/** Board 18 C's menu: 300 wide, 12 radius, items with a 17 px glyph, the title and a line under it. */
function LineMenu({ anchor, entries, asDrawn, onClose }: {
  anchor: HTMLElement
  entries: MenuEntry[]
  asDrawn: string | undefined
  onClose: () => void
}) {
  const box = useRef<HTMLDivElement>(null)
  const [at, setAt] = useState<{ left: number; top: number } | null>(null)
  useDismiss(true, onClose, box, 40)
  useLayoutEffect(() => {
    const node = box.current
    if (!node) return
    const placed = placeFloating({
      anchor: anchor.getBoundingClientRect(),
      width: 300,
      wanted: node.getBoundingClientRect().height,
      align: 'right',
    })
    setAt({ left: Math.round(placed.left), top: Math.round(placed.top) })
  }, [anchor])

  return createPortal((
    <div
      ref={box}
      role="menu"
      data-overlay-layer={40}
      data-as-drawn={asDrawn}
      style={{ left: at?.left ?? 0, top: at?.top ?? 0 }}
      className={cn(
        'fixed z-40 w-[300px] rounded-[12px] border border-line bg-surface py-1 leading-[normal] shadow-[0_14px_40px_rgba(20,20,10,.14)]',
        !at && 'pointer-events-none opacity-0',
      )}
    >
      {entries.map((entry, index) => (entry === 'divider'
        ? <div key={index} className="my-1 h-px bg-line" />
        : (
          <button
            key={index}
            type="button"
            role="menuitem"
            onClick={() => { onClose(); entry.run() }}
            className="mx-1 flex w-[calc(100%-8px)] items-center gap-[11px] rounded-[9px] px-3 py-[9px] text-left hover:bg-hover"
          >
            {entry.glyph && <span className="inline-flex text-text-muted"><VisitGlyph name={entry.glyph} size={17} stroke={2} /></span>}
            <span className="flex flex-col">
              <b className="text-[13px] font-semibold text-text">{entry.title}</b>
              {entry.sub && <span className="text-[11.5px] text-text-muted">{entry.sub}</span>}
            </span>
          </button>
        )))}
    </div>
  ), document.body)
}


